var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { debug } from '@sitecore-jss/sitecore-jss';
import { getPersonalizedRewrite } from '@sitecore-jss/sitecore-jss/personalize';
/** @private */
export const languageError = 'The list of languages cannot be empty';
export const siteError = 'The service needs a site name';
/**
 * @param {string} siteName to inject into error text
 * @private
 */
export function getSiteEmptyError(siteName) {
    return `Site "${siteName}" does not exist or site item tree is missing`;
}
const languageEmptyError = 'The language must be a non-empty string';
/**
 * GQL query made dynamic based on schema differences between SXP and XM Cloud
 * @param {boolean} usesPersonalize flag to detrmine which variation of a query to run
 * @returns GraphQL query to fetch site paths with
 */
const defaultQuery = (usesPersonalize) => /* GraphQL */ `
query ${usesPersonalize ? 'PersonalizeSitemapQuery' : 'DefaultSitemapQuery'}(
  $siteName: String!
  $language: String!
  $includedPaths: [String]
  $excludedPaths: [String]
  $pageSize: Int = 100
  $after: String
) {
  site {
    siteInfo(site: $siteName) {
      routes(
        language: $language
        includedPaths: $includedPaths
        excludedPaths: $excludedPaths
        first: $pageSize
        after: $after
      ){
        total
        pageInfo {
          endCursor
          hasNext
        }
        results {
          path: routePath
          ${usesPersonalize
    ? `
              route {
                personalization {
                  variantIds
                }
              }`
    : ''}
        }
      }
    }
  }
}
`;
/**
 * Service that fetches the list of site pages using Sitecore's GraphQL API.
 * Used to handle a single site
 * This list is used for SSG and Export functionality.
 * @mixes SearchQueryService<PageListQueryResult>
 */
export class BaseGraphQLSitemapService {
    /**
     * Creates an instance of graphQL sitemap service with the provided options
     * @param {GraphQLSitemapServiceConfig} options instance
     */
    constructor(options) {
        this.options = options;
        this._graphQLClient = this.getGraphQLClient();
    }
    /**
     * GraphQL client accessible by descendant classes when needed
     */
    get graphQLClient() {
        return this._graphQLClient;
    }
    /**
     * Gets the default query used for fetching the list of site pages
     */
    get query() {
        return defaultQuery(this.options.includePersonalizedRoutes);
    }
    /**
     * Fetch sitemap which could be used for generation of static pages during `next export`.
     * The `locale` parameter will be used in the item query, but since i18n is not supported,
     * the output paths will not include a `language` property.
     * @param {string} locale which application supports
     * @returns an array of @see StaticPath objects
     */
    fetchExportSitemap(locale) {
        return __awaiter(this, void 0, void 0, function* () {
            const formatPath = (path) => ({
                params: {
                    path,
                },
            });
            return this.fetchSitemap([locale], formatPath);
        });
    }
    /**
     * Fetch sitemap which could be used for generation of static pages using SSG mode
     * @param {string[]} locales locales which application supports
     * @returns an array of @see StaticPath objects
     */
    fetchSSGSitemap(locales) {
        return __awaiter(this, void 0, void 0, function* () {
            const formatPath = (path, locale) => ({
                params: {
                    path,
                },
                locale,
            });
            return this.fetchSitemap(locales, formatPath);
        });
    }
    getTranformedPaths(siteName, languages, formatStaticPath) {
        return __awaiter(this, void 0, void 0, function* () {
            const paths = new Array();
            for (const language of languages) {
                if (language === '') {
                    throw new RangeError(languageEmptyError);
                }
                debug.sitemap('fetching sitemap data for %s %s', language, siteName);
                const results = yield this.fetchLanguageSitePaths(language, siteName);
                const transformedPaths = yield this.transformLanguageSitePaths(results, formatStaticPath, language);
                paths.push(...transformedPaths);
            }
            return paths;
        });
    }
    transformLanguageSitePaths(sitePaths, formatStaticPath, language) {
        return __awaiter(this, void 0, void 0, function* () {
            const formatPath = (path) => formatStaticPath(path.replace(/^\/|\/$/g, '').split('/'), language);
            const aggregatedPaths = [];
            sitePaths.forEach((item) => {
                var _a, _b, _c;
                if (!item)
                    return;
                aggregatedPaths.push(formatPath(item.path));
                const variantIds = (_c = (_b = (_a = item.route) === null || _a === void 0 ? void 0 : _a.personalization) === null || _b === void 0 ? void 0 : _b.variantIds) === null || _c === void 0 ? void 0 : _c.filter((variantId) => !variantId.includes('_') // exclude component A/B test variants
                );
                if (variantIds === null || variantIds === void 0 ? void 0 : variantIds.length) {
                    aggregatedPaths.push(...variantIds.map((varId) => formatPath(getPersonalizedRewrite(item.path, [varId]))));
                }
            });
            return aggregatedPaths;
        });
    }
    fetchLanguageSitePaths(language, siteName) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b, _c, _d;
            const args = {
                siteName: siteName,
                language: language,
                pageSize: this.options.pageSize,
                includedPaths: this.options.includedPaths,
                excludedPaths: this.options.excludedPaths,
            };
            let results = [];
            let hasNext = true;
            let after = '';
            while (hasNext) {
                const fetchResponse = yield this.graphQLClient.request(this.query, Object.assign(Object.assign({}, args), { after }));
                if (!((_a = fetchResponse === null || fetchResponse === void 0 ? void 0 : fetchResponse.site) === null || _a === void 0 ? void 0 : _a.siteInfo)) {
                    throw new RangeError(getSiteEmptyError(siteName));
                }
                else {
                    results = results.concat((_b = fetchResponse.site.siteInfo.routes) === null || _b === void 0 ? void 0 : _b.results);
                    hasNext = (_c = fetchResponse.site.siteInfo.routes) === null || _c === void 0 ? void 0 : _c.pageInfo.hasNext;
                    after = (_d = fetchResponse.site.siteInfo.routes) === null || _d === void 0 ? void 0 : _d.pageInfo.endCursor;
                }
            }
            return results;
        });
    }
    /**
     * Gets a GraphQL client that can make requests to the API. Uses graphql-request as the default
     * library for fetching graphql data (@see GraphQLRequestClient). Override this method if you
     * want to use something else.
     * @returns {GraphQLClient} implementation
     */
    getGraphQLClient() {
        if (!this.options.clientFactory) {
            throw new Error('clientFactory needs to be provided when initializing GraphQL client.');
        }
        return this.options.clientFactory({
            debugger: debug.sitemap,
        });
    }
}
